import {
  VOICE_TERMINAL_STATUSES,
  type VoiceEdit,
  type VoiceJob,
  type VoiceProposalItem,
  type VoiceQuoteFields,
} from '@worki/shared';
import { api } from './api.js';

/** VOICE-001 — the three voice routes, typed once. */
export const voiceApi = {
  create(
    audio: Blob,
    opts?:
      | { quoteId: string; snapshot: Array<{ description: string; quantity: number; unitPrice: number }> }
      | { kind: 'dictate' },
  ): Promise<VoiceJob> {
    const form = new FormData();
    form.append('audio', audio, audio.type.includes('mp4') ? 'note.m4a' : 'note.webm');
    if (opts && 'kind' in opts) {
      form.append('kind', opts.kind);
    } else if (opts) {
      form.append('quoteId', opts.quoteId);
      form.append('snapshot', JSON.stringify(opts.snapshot));
    }
    return api.upload<VoiceJob>('/voice/jobs', form);
  },
  get: (id: string) => api.get<VoiceJob>(`/voice/jobs/${id}`),
  cancel: (id: string) => api.del<VoiceJob>(`/voice/jobs/${id}`),
  /** The recorder opened: the server starts the GPU while the note is spoken. */
  warm: () => api.post<void>('/voice/warm', {}),
};

/**
 * The quote editor has its own orb, which writes into the quote on screen. The
 * top bar's would start a different, new quote and navigate away from unsaved
 * edits — so on these two addresses only the editor's is shown.
 */
export function isQuoteEditorPath(pathname: string): boolean {
  return pathname === '/quotes/new' || /^\/quotes\/[^/]+\/edit\/?$/.test(pathname);
}

/**
 * A job that has not ended. Leaving the voice screen — by its close button or
 * any other way out — cancels one, so it is not run and counted for nobody.
 */
export function jobStillRunning(jobId: string | null, job: VoiceJob | undefined): jobId is string {
  return jobId !== null && (!job || !VOICE_TERMINAL_STATUSES.includes(job.status));
}

export type VoiceFlag = 'estimated' | 'missing' | 'mismatch';
export interface VoiceRow {
  description: string;
  quantity: number;
  unitPrice: number;
  flag: VoiceFlag | null;
}

/** A proposed line as an editor row. The flag is what the row is outlined with. */
export function rowFromItem(item: VoiceProposalItem): VoiceRow {
  const flag: VoiceFlag | null = item.amountMismatch
    ? 'mismatch'
    : item.priceSource === 'missing'
      ? 'missing'
      : item.priceSource === 'history'
        ? 'estimated'
        : null;
  return { description: item.description, quantity: item.quantity, unitPrice: item.unitPrice ?? 0, flag };
}

/**
 * 04.10 — the rest of the quote as said, as form values to set.
 *
 * A pure list rather than calls on the form, so a test can read it. Only what
 * was said is in it: a field the note did not mention keeps what the owner
 * typed. The work address and a note go under what the notes-and-terms text
 * already holds, never over it, and a line it already has is not added twice
 * — the same note opened again must not write its address a second time.
 */
export function fieldValues(
  fields: VoiceQuoteFields | undefined,
  current: { customer: Record<string, string>; legalText: string },
  addressLine: (address: string) => string,
): Array<[string, unknown]> {
  if (!fields) return [];
  const out: Array<[string, unknown]> = [];
  if (fields.customer) out.push(['customer', { ...current.customer, ...fields.customer }]);
  const lines = [fields.workAddress ? addressLine(fields.workAddress) : null, fields.note ?? null].filter(
    (line): line is string => Boolean(line) && !current.legalText.split('\n').includes(line!),
  );
  // Saved terms are a block of their own; already in the text, they stay once.
  if (fields.terms && !current.legalText.includes(fields.terms.trim())) lines.push(fields.terms.trim());
  if (lines.length > 0) out.push(['legalText', [current.legalText, ...lines].filter(Boolean).join('\n')]);
  if (fields.discount) {
    out.push(['discountType', fields.discount.type], ['discountValue', fields.discount.value]);
    if (fields.discount.label) out.push(['discountLabel', fields.discount.label]);
  }
  if (fields.vatRate !== undefined) out.push(['vatRate', fields.vatRate]);
  if (fields.validDays !== undefined) out.push(['validDays', fields.validDays]);
  if (fields.followUpHours !== undefined) out.push(['followUpHours', fields.followUpHours]);
  return out;
}

/**
 * Applies spoken edits to the rows the job was sent with.
 *
 * Positions refer to that snapshot, so removals are applied last: "remove the
 * first line and change the third to 350" means the third line as it was
 * spoken about, not the third after the first is gone. A position past the end
 * — the owner deleted rows while waiting — is skipped and counted, never
 * applied to whichever row happens to sit there now.
 */
export function applyEdits(
  rows: Array<{ description: string; quantity: number; unitPrice: number }>,
  edits: VoiceEdit[],
): { rows: VoiceRow[]; skipped: number; touched: number[] } {
  const next: Array<VoiceRow | null> = rows.map((r) => ({ ...r, flag: null }));
  const removed = new Set<number>();
  const touched = new Set<number>();
  const added: VoiceRow[] = [];
  let skipped = 0;
  for (const edit of edits) {
    if (edit.op === 'add') {
      added.push(rowFromItem(edit.item));
      continue;
    }
    if (edit.position < 0 || edit.position >= next.length) {
      skipped += 1;
      continue;
    }
    if (edit.op === 'remove') {
      removed.add(edit.position);
      continue;
    }
    const row = next[edit.position]!;
    // A new price nobody said is outlined like a new line's: red, "check the
    // amount". A proposal saved before the field existed has no flag at all,
    // and that reads as heard.
    next[edit.position] = {
      ...row,
      description: edit.description ?? row.description,
      quantity: edit.quantity ?? row.quantity,
      unitPrice: edit.unitPrice ?? row.unitPrice,
      flag: edit.amountMismatch ? 'mismatch' : row.flag,
    };
    touched.add(edit.position);
  }
  const kept: VoiceRow[] = [];
  const touchedAfter: number[] = [];
  next.forEach((row, i) => {
    if (removed.has(i) || !row) return;
    if (touched.has(i)) touchedAfter.push(kept.length);
    kept.push(row);
  });
  for (const row of added) {
    touchedAfter.push(kept.length);
    kept.push(row);
  }
  return { rows: kept, skipped, touched: touchedAfter };
}
